import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
} from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteTemplates, fetchTemplateList } from '~features/templates/api'
import { Templates } from '~features/templates/types'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import {
  fetchAllDivision,
  fetchProductList,
} from '~in-app/shareable/index.api'
import { Division } from '~in-app/types'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/products/templates/list')({
  component: TemplatePage,
  loader: async ({ abortController }) => {
    let divisionIds: Option[] = []
    let productIds: Option[] = []
    const privilegeValue = auth$.privileges.get()
    const defaultDivisionList = privilegeValue.has('0.4.0.0')
      ? await fetchAllDivision({
        abortCtrl: abortController,
      })
      : {} as { divisions: Division[] }
    const flattenDivisions = (
      divisions: any[],
      parentId: string | null = null,
    ): any[] => {
      return divisions.reduce((acc: any[], division: any) => {
        acc.push({
          value: division.id,
          label: division.name,
          parentId: parentId,
        })
        if (division.mychilds && division.mychilds.length > 0) {
          acc = acc.concat(flattenDivisions(division.mychilds, division.id))
        }
        return acc
      }, [])
    }
    const productList = await fetchProductList({
      abortCtrl: abortController,
    })
    productIds = productList?.list.map((item: any) => ({
      value: item.id,
      label: item.name,
    }))
    divisionIds = flattenDivisions(defaultDivisionList.divisions)
    const defaultTemplateListing = privilegeValue.has('0.3.8.16')
      ? await fetchTemplateList({
        abortCtrl: abortController,
        query: {
          page: (() => {
            const match = new URLSearchParams(window.location.search).get('page')
            return match ? parseInt(match, 10) - 1 : 0
          })(),
          size: (() => {
            const match = new URLSearchParams(window.location.search).get('pageSize')
            return match ? parseInt(match, 10) : 20
          })(),
          queries: new URLSearchParams(window.location.search).get('queries') || undefined,
          divisionIds: new URLSearchParams(window.location.search).get('divisionIds') || undefined,
          productIds: new URLSearchParams(window.location.search).get('productIds') || undefined,
          sort: (defaultState$.sortList.get() || [])
            .map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`)
            .join(','),
        },
      })
      : ({} as ListingResponse<Templates>)

    return { divisionIds, productIds, privilegeValue, defaultTemplateListing }
  },
  staticData: {
    privileges: ['0.3.8.16'],
  },
})
const columnHelper = createColumnHelper<Templates>()
const defaultState$ = observable<{
  isOpenDeleteTemplate: boolean
  deleteTemplateId: string | undefined
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  productIds: string[] | undefined
  divisionIds: string[] | undefined
  isChange: boolean
  deleteTemplateName: string | undefined
}>({
  sortList: [{ id: 'name', desc: true }],
  pagination: 0,
  pageSize: 20,
  queries: '',
  productIds: undefined,
  divisionIds: undefined,
  isChange: false,
  isOpenDeleteTemplate: false,
  deleteTemplateId: undefined,
  deleteTemplateName: undefined,
})

function TemplatePage() {
  const { divisionIds, productIds, privilegeValue, defaultTemplateListing }
    = useLoaderData({
      from: '/console/products/templates/list',
    })
  const navigate = Route.useNavigate()

  const dropdownItems = (value: Templates) => {
    return [
      {
        label: 'Edit',
        onClick: () =>
          navigate({ to: `/console/products/templates/edit/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.8.18'),
        icon: (
          <Icon
            height="16"
            icon="lucide:pencil"
            width="16"
          />
        ),
      },

      {
        label: 'Duplicate',
        onClick: () =>
          navigate({ to: `/console/products/templates/duplicate/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.8.17'),
        icon: (
          <Icon
            height="16"
            icon="mdi:content-duplicate"
            width="16"
          />
        ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteTemplate(value),
        disabled: !privilegeValue?.has('0.3.8.19'),
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Template Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('product.name', {
      cell: info => info.getValue(),
      header: () => <span>Product</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable(defaultState$)
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.productIds.set(new URLSearchParams(window.location.search).get('productIds')?.split(','))
    state$.divisionIds.set(new URLSearchParams(window.location.search).get('divisionIds')?.split(','))
    state$.queries.set(filteredQueries)

    const disposer = observe(
      state$,
      (e) => {
        if (e.previous !== undefined && e.value !== undefined) {
          refetch()
        }
      },
      { immediate: false },
    )

    return disposer
  }, [])

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
    {
      label: 'Division',
      type: 'dropdown',
      isMultiSelect: true,
      options: divisionIds,
      value: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
      result: divisionIds.filter(divisions => new URLSearchParams(window.location.search).get('divisionIds')?.split(',').includes(divisions.value)),
    },
    {
      label: 'Product',
      type: 'dropdown',
      isMultiSelect: true,
      options: productIds,
      value: productIds.filter(products => new URLSearchParams(window.location.search).get('productIds')?.split(',').includes(products.value)),
      result: productIds.filter(products => new URLSearchParams(window.location.search).get('productIds')?.split(',').includes(products.value)),
    },
  ]
  const tableState$ = useObservable<TableState$<Templates>>(() => ({
    id: 'templates-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pagination: state$.pagination.get(),
    pageSize: state$.pageSize.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}
      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      const divisionFilter = value.find((item: Option) => item.label === 'Division')
      const divisionValue = divisionFilter?.result || []
      if (divisionValue.length > 0) {
        state$.divisionIds.set(divisionValue.map((item: { value: any }) => item.value))
        searchParams.divisionIds = divisionValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.divisionIds.set(undefined)
        searchParams.divisionIds = undefined
      }
      const productFilter = value.find((item: Option) => item.label === 'Product')
      const productValue = productFilter?.result || []
      if (productValue.length > 0) {
        state$.productIds.set(productValue.map((item: { value: any }) => item.value))
        searchParams.productIds = productValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.productIds.set(undefined)
        searchParams.productIds = undefined
      }
      state$.isChange.set(!state$.isChange.get())
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(divisionValue.length > 0)) delete updatedSearch.divisionIds
          if (!(productValue.length > 0)) delete updatedSearch.productIds

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const useDeleteTemplate = () => {
    const mutation = useMutation({
      mutationFn: deleteTemplates,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['template-listing'] })
        toast.success('Templates deleted successfully', {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 1000,
        })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteTemplate()

  const handleDeleteTemplate = (value: Templates) => {
    state$.deleteTemplateId.set(value.id)
    state$.deleteTemplateName.set(value.name)
    state$.isOpenDeleteTemplate.set(true)
  }

  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['template-listing'],
    queryFn: async ({ signal }) => {
      const response = await fetchTemplateList({
        // abortCtrl: signal,
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          divisionIds: state$?.divisionIds?.get()
            ? (state$?.divisionIds?.get() ?? []).join(',')
            : undefined,
          productIds: state$?.productIds?.get()
            ? (state$?.productIds?.get() ?? []).join(',')
            : undefined,
          sort: state$.sortList
            .get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newTemplateList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      })) as Templates[]
      tableState$.data.set(newTemplateList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
    initialData: defaultTemplateListing,
  })
  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Templates Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteTemplate.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteTemplateName.get()}' Template?`}
                onClose={() => { state$.isOpenDeleteTemplate.set(false), state$.deleteTemplateId.set(undefined), state$.deleteTemplateName.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteRoleId = state$.deleteTemplateId.get()
                    if (deleteRoleId) {
                      deleteMutation.mutate(deleteRoleId as string)
                      state$.deleteTemplateId.set(undefined)
                      state$.deleteTemplateName.set(undefined)
                      state$.isOpenDeleteTemplate.set(false)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue.has('0.3.8.17')}
          onClick={() => navigate({ to: '/console/products/templates/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
